import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useBilling } from '../../context/BillingContext';
import BillingShell from '../../layouts/BillingShell';

// Derives a plan description from the three differentiating DB fields only.
// CONSTRAINT 16: featureSummary is unreliable — never use it. Source of truth is PlanConfig.
const planDesc = (p) => {
  const parts = [];
  if (p.maxHealers   != null) parts.push(`${p.maxHealers} ${p.maxHealers === 1 ? 'healer' : 'healers'}`);
  if (p.maxPatients  != null) parts.push(`${p.maxPatients} ${p.maxPatients === 1 ? 'patient' : 'patients'}`);
  if (p.chatQueriesPerMonth != null && p.chatQueriesPerMonth > 0)
    parts.push(`${p.chatQueriesPerMonth} AI sessions/month`);
  return parts.join(' · ') || null;
};

const ConfirmModal = ({ title, body, error, confirmLabel, confirmColor, loading, onConfirm, onCancel }) => (
  <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
    <div style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '420px', padding: '28px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
      <div style={{ fontSize: '16px', fontWeight: '700', color: '#1A1A2E', marginBottom: '10px' }}>{title}</div>
      <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', marginBottom: error ? '12px' : '24px' }}>{body}</div>
      {error && <div style={{ fontSize: '12px', color: '#B00020', marginBottom: '16px' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} disabled={loading}
          style={{ padding: '10px 20px', borderRadius: '7px', border: '1px solid #ddd', background: '#fff', color: '#555', fontSize: '13px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}>
          Cancel
        </button>
        <button type="button" onClick={onConfirm} disabled={loading}
          style={{ padding: '10px 20px', borderRadius: '7px', border: 'none', background: confirmColor, color: '#fff', fontSize: '13px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', minHeight: '44px', opacity: loading ? 0.6 : 1 }}>
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

const BillingDashboard = () => {
  const { billingAxios, isAuthenticated } = useBilling();
  const [planData, setPlanData]           = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [pendingPlan, setPendingPlan]     = useState(null);
  const [isSubmitting, setIsSubmitting]   = useState(false);
  const [actionError, setActionError]     = useState(null);
  const [successMsg, setSuccessMsg]       = useState(null);
  const [invoices, setInvoices]               = useState(null);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError]     = useState(null);
  const [downloadingId, setDownloadingId]     = useState(null);
  const [invoiceRetry, setInvoiceRetry]       = useState(0);
  const [downloadError, setDownloadError]     = useState(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    const load = async () => {
      try {
        const planRes = await billingAxios.get('/billing/plan-options');
        setPlanData(planRes.data.data);
      } catch {
        setError('Failed to load billing information. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated, billingAxios]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated) return;
    const load = async () => {
      setInvoicesLoading(true);
      setInvoicesError(null);
      try {
        const res = await billingAxios.get('/subscription/invoices');
        setInvoices(res.data.data?.slice(0, 12) || []);
      } catch {
        setInvoicesError('Unable to load invoices.');
      } finally {
        setInvoicesLoading(false);
      }
    };
    load();
  }, [isAuthenticated, invoiceRetry, billingAxios]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownloadInvoice = useCallback(async (id, invoiceNumber) => {
    setDownloadingId(id);
    setDownloadError(null);
    try {
      const res = await billingAxios.get(
        `/subscription/invoice/${id}`,
        { responseType: 'arraybuffer' }
      );
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        const blob = new Blob(
          [new Uint8Array(res.data)],
          { type: 'application/pdf' }
        );
        const file = new File(
          [blob],
          `invoice-${invoiceNumber || id}.pdf`,
          { type: 'application/pdf' }
        );
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: `Invoice ${invoiceNumber || id}`,
          });
        } else {
          const base64 = btoa(
            new Uint8Array(res.data).reduce(
              (data, byte) => data + String.fromCharCode(byte), ''
            )
          );
          window.open(`data:application/pdf;base64,${base64}`, '_blank');
        }
      } else {
        const blob = new Blob(
          [new Uint8Array(res.data)],
          { type: 'application/pdf' }
        );
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoice-${invoiceNumber || id}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.dispatchEvent(
          new MouseEvent('click', { bubbles: false, cancelable: true })
        );
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (err) {
      setDownloadError('Download failed. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  }, [billingAxios]);

  const handleUpgradeClick = (plan) => {
    setPendingPlan(plan);
    setActionError(null);
    setConfirmAction('upgrade');
  };

  const handleCancelClick = () => {
    setActionError(null);
    setConfirmAction('cancel');
  };

  const handleDowngradeClick = (plan) => {
    setPendingPlan(plan);
    setActionError(null);
    setConfirmAction('downgrade');
  };

  const handleResubscribeClick = () => {
    setPendingPlan(resubscribeOption);
    setActionError(null);
    setConfirmAction('resubscribe');
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    let tab; // hoisted — accessible in catch to close orphaned blank tab on API error
    try {
      if (confirmAction === 'upgrade') {
        tab = window.open('', '_blank'); // synchronous — before any await, preserves user-gesture context
        if (!tab) {
          setActionError('Your browser blocked the payment window. Please allow popups for this site and try again.');
          return;
        }
        const hasActiveSub = planData?.subscriptionStatus === 'active';
        let res;
        if (hasActiveSub) {
          res = await billingAxios.post('/subscription/change-plan', { newPlan: pendingPlan.planName });
        } else {
          res = await billingAxios.post('/billing/subscribe', { planName: pendingPlan.planName });
        }
        const shortUrl = res.data.data?.shortUrl;
        if (shortUrl && shortUrl.startsWith('https://rzp.io/')) {
          tab.location.href = shortUrl;
        } else {
          tab.close();
          setActionError('Unexpected payment URL. Please try again.');
        }
        setConfirmAction(null); // always clear modal after both shortUrl branches
      } else if (confirmAction === 'resubscribe') {
        tab = window.open('', '_blank');
        if (!tab) {
          setActionError('Your browser blocked the payment window. Please allow popups for this site and try again.');
          return;
        }
        const res = await billingAxios.post('/billing/subscribe', { planName: pendingPlan.planName });
        const shortUrl = res.data.data?.shortUrl;
        if (shortUrl && shortUrl.startsWith('https://rzp.io/')) {
          tab.location.href = shortUrl;
        } else {
          tab.close();
          setActionError('Unexpected payment URL. Please try again.');
        }
        setConfirmAction(null);
      } else if (confirmAction === 'downgrade') {
        const res = await billingAxios.post('/subscription/downgrade', { newPlan: pendingPlan.planName });
        setConfirmAction(null);
        try {
          const planRes = await billingAxios.get('/billing/plan-options');
          setPlanData(planRes.data.data);
        } catch {
          /* silent — planData stays stale; user can refresh */
        }
        if (res.status === 207) {
          setSuccessMsg('Plan downgraded. Your refund could not be processed — please contact support.');
        } else {
          setSuccessMsg(
            (pendingPlan?.proratedRefundAmount ?? 0) > 0
              ? `Downgraded to ${pendingPlan.planName}. Refund of ₹${(pendingPlan.proratedRefundAmount).toLocaleString('en-IN')} will be processed within 5–7 business days.`
              : `Downgraded to ${pendingPlan.planName}. Your billing will update at the next renewal cycle.`
          );
        }
      } else {
        await billingAxios.post('/billing/cancel');
        setConfirmAction(null);
        try {
          const planRes = await billingAxios.get('/billing/plan-options');
          setPlanData(planRes.data.data);
        } catch {
          setPlanData(prev => ({ ...prev, subscriptionStatus: 'cancelled' }));
        }
        setSuccessMsg('Subscription cancelled. Access continues until your billing period ends.');
      }
    } catch (err) {
      if (tab && !tab.closed) tab.close(); // close orphaned blank tab if API call threw
      console.error('[BillingDashboard] handleConfirm error', { action: confirmAction, message: err?.message });
      setActionError(err?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) return <Navigate to="/billing" replace />;

  if (loading) {
    return <BillingShell><p style={{ color: '#555', fontSize: '15px' }}>Loading your billing information...</p></BillingShell>;
  }

  if (error) {
    return <BillingShell><p style={{ color: '#B00020', fontSize: '15px' }}>{error}</p></BillingShell>;
  }

  const { currentPlan, upgradePlans, downgradePlans, subscriptionStatus, subscriptionGracePeriodEnd, subscriptionLocked, resubscribeOption } = planData;
  const canCancel    = subscriptionStatus === 'active' || subscriptionStatus === 'created';
  const validUntil   = currentPlan?.subscriptionValidUntil ? new Date(currentPlan.subscriptionValidUntil) : null;
  const isPaymentLink = !subscriptionStatus;
  const plStillValid  = isPaymentLink && validUntil && validUntil > new Date();
  const plExpired     = isPaymentLink
    && !['FREE', 'SOLO_FREE'].includes(currentPlan?.planName)
    && (!validUntil || validUntil <= new Date());
  const statusLabel = subscriptionStatus
    ? subscriptionStatus.charAt(0).toUpperCase() + subscriptionStatus.slice(1)
    : plStillValid ? 'Active'
    : plExpired    ? 'Expired'
    : 'Free';
  const statusColor = (canCancel || plStillValid) ? '#27ae60'
    : plExpired ? '#EF4444'
    : (subscriptionStatus ? '#888' : '#2ABFAB');
  const graceDaysLeft = subscriptionGracePeriodEnd
    ? Math.max(1, Math.ceil((new Date(subscriptionGracePeriodEnd) - Date.now()) / (24 * 60 * 60 * 1000)))
    : null;

  return (
    <BillingShell>
      {confirmAction && (
        <ConfirmModal
          title={
            confirmAction === 'upgrade'      ? `Upgrade to ${pendingPlan?.planName}?` :
            confirmAction === 'resubscribe'  ? `Resubscribe to ${pendingPlan?.planName}?` :
            confirmAction === 'downgrade'    ? `Downgrade to ${pendingPlan?.planName}?` :
            'Cancel Subscription?'
          }
          body={
            confirmAction === 'upgrade' ? (() => {
              const fullPrice   = pendingPlan?.gstInclusivePrice ?? 0;
              const prorated    = pendingPlan?.proratedUpgradeAmount;
              const credit      = pendingPlan?.creditAmount ?? 0;
              const daysLeft    = pendingPlan?.daysRemainingForProration;
              const nextRenewal = pendingPlan?.nextRenewalDate
                ? new Date(pendingPlan.nextRenewalDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                : null;
              const row = (label, value, bold) => (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', ...(bold && { fontWeight: '700', borderTop: '1px solid #E5E7EB', paddingTop: '8px', marginTop: '2px' }) }}>
                  <span>{label}</span><span>{value}</span>
                </div>
              );
              if (prorated === 0) {
                return (
                  <div style={{ fontSize: '13px', color: '#555' }}>
                    <p style={{ marginBottom: '8px' }}>Free upgrade for remaining period.</p>
                    {nextRenewal && <p style={{ marginBottom: 0 }}>Next renewal: {nextRenewal} — ₹{fullPrice.toLocaleString('en-IN')}/month</p>}
                  </div>
                );
              }
              if (prorated == null || prorated === fullPrice) {
                return (
                  <>
                    <div style={{ fontSize: '13px', color: '#555' }}>
                      {row('Base price', `₹${(pendingPlan?.monthlyPriceINR ?? 0).toLocaleString('en-IN')}/month`)}
                      {row(`GST (${pendingPlan?.gstPercent}%)`, `₹${(pendingPlan?.gstAmount ?? 0).toLocaleString('en-IN')}`)}
                      {row('Total', `₹${fullPrice.toLocaleString('en-IN')}/month`, true)}
                    </div>
                    <p style={{ fontSize: '13px', color: '#555', marginTop: '12px', marginBottom: 0 }}>After payment, tap the back button to return to the app.</p>
                  </>
                );
              }
              return (
                <>
                  <div style={{ fontSize: '13px', color: '#555' }}>
                    {row('New plan (full price)', `₹${fullPrice.toLocaleString('en-IN')}/month`)}
                    {row(`Credit for unused days${daysLeft ? ` (${daysLeft} days)` : ''}`, `-₹${credit.toLocaleString('en-IN')}`)}
                    {row('Amount due today', `₹${prorated.toLocaleString('en-IN')}`, true)}
                    {nextRenewal && row('Next renewal', `${nextRenewal} — ₹${fullPrice.toLocaleString('en-IN')}/month`)}
                  </div>
                  <p style={{ fontSize: '13px', color: '#555', marginTop: '12px', marginBottom: 0 }}>After payment, tap the back button to return to the app.</p>
                </>
              );
            })() :
          confirmAction === 'resubscribe' ? (
            <div style={{ fontSize: '13px', color: '#555' }}>
              <p style={{ marginBottom: '8px' }}>
                You will be charged <strong>₹{(pendingPlan?.gstInclusivePrice ?? 0).toLocaleString('en-IN')}/month</strong> (includes {pendingPlan?.gstPercent}% GST) to restore your <strong>{pendingPlan?.planName}</strong> plan.
              </p>
              <p style={{ marginBottom: 0 }}>After payment, tap the back button to return to the app.</p>
            </div>
          ) :
          confirmAction === 'downgrade' ? (() => {
            const refund      = pendingPlan?.proratedRefundAmount ?? 0;
            const healerWarn  = pendingPlan?.maxHealers  != null && currentPlan.activeHealerCount  > pendingPlan.maxHealers;
            const patientWarn = pendingPlan?.maxPatients != null && currentPlan.activePatientCount > pendingPlan.maxPatients;
            return (
              <div style={{ fontSize: '13px', color: '#555' }}>
                <p style={{ marginBottom: '8px' }}>Your plan will change from <strong>{currentPlan.planName}</strong> to <strong>{pendingPlan?.planName}</strong>.</p>
                {refund > 0
                  ? <p style={{ marginBottom: '8px' }}>Estimated refund: <strong>₹{refund.toLocaleString('en-IN')}</strong> — processed within 5–7 business days.</p>
                  : <p style={{ marginBottom: '8px' }}>Your billing will update at the next renewal cycle. No immediate refund.</p>
                }
                {(healerWarn || patientWarn) && (
                  <p style={{ color: '#B00020', marginBottom: 0 }}>
                    {healerWarn && `You have ${currentPlan.activeHealerCount} active healers — ${pendingPlan.planName} allows ${pendingPlan.maxHealers}. `}
                    {patientWarn && `You have ${currentPlan.activePatientCount} active patients — ${pendingPlan.planName} allows ${pendingPlan.maxPatients}. `}
                    Excess accounts will not be removed but access may be restricted.
                  </p>
                )}
                {plStillValid && (
                  <p style={{ marginBottom: 0, color: '#555' }}>
                    Access continues at the lower plan until {validUntil?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}. A prorated refund may be issued within 5–7 business days.
                  </p>
                )}
              </div>
            );
          })() :
          plStillValid
            ? `Your plan will not renew and access will end on ${validUntil.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}. No immediate refund.`
            : 'Your access continues until the end of the current billing period.'
          }
          error={actionError}
          confirmLabel={
            confirmAction === 'upgrade' || confirmAction === 'resubscribe' ? 'Proceed to Payment' :
            confirmAction === 'downgrade' ? 'Confirm Downgrade' :
            'Cancel Subscription'
          }
          confirmColor={confirmAction === 'cancel' || confirmAction === 'downgrade' ? '#B00020' : '#2ABFAB'}
          loading={isSubmitting}
          onConfirm={handleConfirm}
          onCancel={() => { setConfirmAction(null); setActionError(null); }}
        />
      )}

      {subscriptionLocked && (
        <div style={{ background: '#B00020', color: '#fff', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px', fontSize: '14px', fontWeight: '600' }}>
          Your account is locked. New bookings, session logs, and patient invitations are blocked. Please renew your subscription to restore access.
        </div>
      )}
      {!subscriptionLocked && subscriptionGracePeriodEnd && (
        <div style={{ background: '#FFF8E1', border: '1px solid #FFC107', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px', fontSize: '14px', color: '#856404' }}>
          Your subscription has expired. {graceDaysLeft} day{graceDaysLeft === 1 ? '' : 's'} remaining before your account is locked. Renew now to avoid disruption.
        </div>
      )}

      {/* Return to app — persistent; every user who reaches this page arrived from the mobile app */}
      <section style={{ background: '#E0FBF7', border: '1px solid #2ABFAB', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' }}>
        <div style={{ fontSize: '13px', color: '#1A1A2E', lineHeight: '1.6', marginBottom: '14px' }}>
          After completing your payment, tap below to return to the LifeChiAI app.
        </div>
        <button
          type="button"
          onClick={() => { window.location.href = 'lifechiai://payment-complete'; }}
          style={{ background: '#2ABFAB', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}
        >
          Return to LifeChiAI
        </button>
        <p style={{ fontSize: '12px', color: '#888', marginTop: '10px', marginBottom: 0, lineHeight: '1.5' }}>
          If nothing happens, close this browser tab and reopen the LifeChiAI app to see your updated plan.
        </p>
      </section>

      {/* Section 1 — Current Plan */}
      <section style={{ background: '#fff', borderRadius: '12px', padding: '24px', marginBottom: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: '12px', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Current Plan</div>
        <div style={{ fontSize: '22px', fontWeight: '700', color: '#1A1A2E' }}>{currentPlan.planName}</div>
        <div style={{ fontSize: '14px', color: '#555', marginTop: '4px' }}>
          ₹{currentPlan.gstInclusivePrice ?? currentPlan.monthlyPriceINR}/month (inclusive of {currentPlan.gstPercent}% GST)
        </div>
        <div style={{ fontSize: '13px', color: statusColor, marginTop: '8px', fontWeight: '600' }}>
          {statusLabel}
        </div>
        {planDesc(currentPlan) && (
          <div style={{ fontSize: '13px', color: '#555', marginTop: '8px' }}>{planDesc(currentPlan)}</div>
        )}
        {successMsg && <div style={{ marginTop: '12px', fontSize: '13px', color: '#27ae60' }}>{successMsg}</div>}
      </section>

      {/* Section 2 — Downgrade Plan */}
      {downgradePlans?.length > 0 && (subscriptionStatus === 'active' || plStillValid) && (
        <section style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>Downgrade Plan</div>
          {downgradePlans.slice(0, 1).map(plan => {
            const healerWarn  = plan.maxHealers  != null && currentPlan.activeHealerCount  > plan.maxHealers;
            const patientWarn = plan.maxPatients != null && currentPlan.activePatientCount > plan.maxPatients;
            return (
              <div key={plan.planName} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '20px', marginBottom: '12px' }}>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#1A1A2E' }}>{plan.planName}</div>
                <div style={{ fontSize: '14px', color: '#555', margin: '4px 0 8px' }}>
                  {plan.monthlyPriceINR > 0 ? `₹${plan.monthlyPriceINR.toLocaleString('en-IN')} + ${plan.gstPercent}% GST` : 'Free'}
                </div>
                {plan.proratedRefundAmount > 0 && (
                  <div style={{ fontSize: '13px', color: '#27ae60', marginBottom: '8px' }}>
                    Estimated refund: ₹{plan.proratedRefundAmount.toLocaleString('en-IN')}
                  </div>
                )}
                {(healerWarn || patientWarn) && (
                  <div style={{ fontSize: '12px', color: '#B00020', background: '#FFF0F0', borderRadius: '6px', padding: '8px 12px', marginBottom: '12px' }}>
                    {healerWarn && <div>You have {currentPlan.activeHealerCount} active healers — {plan.planName} allows {plan.maxHealers}.</div>}
                    {patientWarn && <div>You have {currentPlan.activePatientCount} active patients — {plan.planName} allows {plan.maxPatients}.</div>}
                    <div style={{ marginTop: '4px' }}>Excess accounts will not be removed but access may be restricted.</div>
                  </div>
                )}
                <button type="button" onClick={() => handleDowngradeClick(plan)}
                  style={{ background: '#fff', color: '#B00020', border: '1.5px solid #B00020', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}>
                  Downgrade to {plan.planName}
                </button>
              </div>
            );
          })}
        </section>
      )}

      {/* State C — Payment-link expired */}
      {plExpired && (
        <section style={{ background: '#FFF8E1', border: '1px solid #FFC107', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#856404' }}>
            {validUntil
              ? `Your plan expired on ${validUntil.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}.`
              : 'Your subscription has ended.'}
          </div>
          <div style={{ fontSize: '13px', color: '#856404', marginTop: '6px' }}>
            Resubscribe below to restore access.
          </div>
        </section>
      )}

      {/* State C — Resubscribe to current plan */}
      {resubscribeOption && (
        <section style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>Resubscribe</div>
          <div style={{ background: '#E0FBF7', border: '1.5px solid #2ABFAB', borderRadius: '12px', padding: '20px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#2ABFAB', textTransform: 'uppercase', marginBottom: '6px' }}>Your Previous Plan</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#1A1A2E' }}>{resubscribeOption.planName}</div>
            <div style={{ fontSize: '14px', color: '#555', margin: '4px 0 8px' }}>₹{(resubscribeOption.monthlyPriceINR ?? 0).toLocaleString('en-IN')} + {resubscribeOption.gstPercent}% GST/month</div>
            {planDesc(resubscribeOption) && <div style={{ fontSize: '13px', color: '#555', marginBottom: '12px' }}>{planDesc(resubscribeOption)}</div>}
            <button type="button" onClick={handleResubscribeClick}
              style={{ background: '#2ABFAB', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}>
              Resubscribe to {resubscribeOption.planName}
            </button>
          </div>
        </section>
      )}

      {/* Section 3 — Upgrade Plans */}
      {upgradePlans?.length > 0 && (
        <section style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>Upgrade Your Plan</div>
          {upgradePlans.map(plan => (
            <div key={plan.planName} style={{ background: plan.highlight ? '#E0FBF7' : '#fff', border: plan.highlight ? '1.5px solid #2ABFAB' : '1px solid #E5E7EB', borderRadius: '12px', padding: '20px', marginBottom: '12px' }}>
              {plan.highlight && <div style={{ fontSize: '11px', fontWeight: '700', color: '#2ABFAB', textTransform: 'uppercase', marginBottom: '6px' }}>Recommended</div>}
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#1A1A2E' }}>{plan.planName}</div>
              <div style={{ fontSize: '14px', color: '#555', margin: '4px 0 8px' }}>₹{(plan.monthlyPriceINR ?? 0).toLocaleString('en-IN')} + {plan.gstPercent}% GST</div>
              {planDesc(plan) && <div style={{ fontSize: '13px', color: '#555', marginBottom: '8px' }}>{planDesc(plan)}</div>}
              {plan.roiMessage && <div style={{ fontSize: '12px', color: '#2ABFAB', fontWeight: '600', marginBottom: '12px' }}>{plan.roiMessage}</div>}
              <button type="button" onClick={() => handleUpgradeClick(plan)}
                style={{ background: '#2ABFAB', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}>
                Upgrade to {plan.planName}
              </button>
            </div>
          ))}
        </section>
      )}

      {/* Section 4 — Cancel Subscription */}
      {(canCancel || plStillValid) && (
        <section style={{ background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#1A1A2E', marginBottom: '8px' }}>Cancel Subscription</div>
          <div style={{ fontSize: '13px', color: '#555', marginBottom: '16px' }}>
            {plStillValid
              ? `Access will continue until ${validUntil.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}. Your plan will not renew.`
              : 'Your access continues until the end of the current billing period.'}
          </div>
          <button type="button" onClick={handleCancelClick}
            style={{ background: '#fff', color: '#B00020', border: '1.5px solid #B00020', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}>
            Cancel Subscription
          </button>
        </section>
      )}

      {/* Section 5 — Invoice History */}
      <section style={{ background: '#fff', borderRadius: '12px', padding: '24px', marginTop: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: '14px', fontWeight: '700', color: '#1A1A2E', marginBottom: '16px' }}>Invoices</div>
        {downloadError && (
          <p style={{ fontSize: '12px', color: '#B00020', margin: '0 0 12px' }}>{downloadError}</p>
        )}
        {invoicesLoading && (
          <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>Loading invoices…</p>
        )}
        {invoicesError && (
          <div>
            <p style={{ fontSize: '13px', color: '#B00020', margin: '0 0 8px' }}>{invoicesError}</p>
            <button type="button" onClick={() => setInvoiceRetry(r => r + 1)}
              style={{ background: 'none', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', color: '#555', cursor: 'pointer' }}>
              Retry
            </button>
          </div>
        )}
        {!invoicesError && invoices && invoices.length === 0 && (
          <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>No invoices yet.</p>
        )}
        {invoices && invoices.length > 0 && (
          <div>
            {invoices.map(inv => (
              <div key={inv._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F0F0F0' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#1A1A2E' }}>
                    {new Date(inv.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                  {/* totalAmount stored in paise — divide by 100 for display */}
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                    ₹{inv.totalAmount != null ? (Number(inv.totalAmount) / 100).toLocaleString('en-IN') : '—'} · {inv.status}
                  </div>
                </div>
                <button type="button" onClick={() => handleDownloadInvoice(inv._id, inv.invoiceNumber)}
                  disabled={downloadingId === inv._id}
                  style={{ background: 'none', border: '1px solid #2ABFAB', color: '#2ABFAB', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: '600', cursor: downloadingId === inv._id ? 'not-allowed' : 'pointer', opacity: downloadingId === inv._id ? 0.6 : 1, minHeight: '36px' }}>
                  {downloadingId === inv._id ? 'Downloading…' : 'Download PDF'}
                </button>
              </div>
            ))}
          </div>
        )}
        <p style={{ fontSize: '12px', color: '#888', marginTop: '16px', lineHeight: '1.5', marginBottom: 0 }}>
          You can also view all invoices in the LifeChiAI mobile app under{' '}
          <strong>Settings → Manage Subscription → View Invoices</strong>.
        </p>
      </section>
    </BillingShell>
  );
};

export default BillingDashboard;
